import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "../hr/theme";
import { Btn, Pill } from "../hr/ui";
import { DataTable, dt } from "./crmui";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function CRMSettings() {
  const [s, setS] = useState(null);
  const [events, setEvents] = useState(null);
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    api.get("/crm/settings").then(setS).catch((e) => toast.error(formatApiError(e)));
    api.get("/crm/events", { params: { page_size: 25 } }).then(setEvents).catch(() => {});
    api.get("/crm/audit", { params: { page_size: 25 } }).then(setAudit).catch(() => {});
  }, []);

  const save = async () => {
    try { setS(await api.put("/crm/settings", s)); toast.success("CRM settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setInt = (k, field, v) => setS({ ...s, integrations: { ...s.integrations, [k]: { ...s.integrations[k], [field]: v } } });

  if (!s) return <div className="h-48 rounded-2xl border bg-white animate-pulse" style={{ borderColor: HC.border }} />;

  return (
    <div className="space-y-4" data-testid="crm-settings-page">
      <div className="rounded-2xl bg-white border p-5 space-y-3" style={{ borderColor: HC.border }}>
        <h3 className="font-brand font-bold text-base" style={{ color: HC.text }}>Lifecycle &amp; loyalty policy</h3>
        <label className="flex items-center gap-2 text-[12.5px]" style={{ color: HC.text }}>
          <input type="checkbox" checked={!!s.probation_required} data-testid="setting-probation"
            onChange={(e) => setS({ ...s, probation_required: e.target.checked })} />
          New customers enter probation — loyalty wallet stays inactive until verified
        </label>
        <label className="flex items-center gap-2 text-[12.5px]" style={{ color: HC.text }}>
          <input type="checkbox" checked={!!s.auto_verify_on_otp} data-testid="setting-otp-verify"
            onChange={(e) => setS({ ...s, auto_verify_on_otp: e.target.checked })} />
          Treat a successful phone OTP as secondary verification
        </label>
        <label className="flex items-center gap-2 text-[12.5px]" style={{ color: HC.text }}>
          <input type="checkbox" checked={!!s.marketing_requires_consent} data-testid="setting-consent"
            onChange={(e) => setS({ ...s, marketing_requires_consent: e.target.checked })} />
          Never send marketing to customers without consent
        </label>
        <div className="flex flex-wrap gap-4">
          <label className="text-[12px]" style={{ color: HC.muted }}>
            Probation days
            <input type="number" value={s.probation_days} data-testid="setting-probation-days"
              onChange={(e) => setS({ ...s, probation_days: Number(e.target.value) })}
              className="h-9 px-2 ml-2 w-20 rounded-md border text-[12.5px]" style={{ borderColor: HC.border }} />
          </label>
          <label className="text-[12px]" style={{ color: HC.muted }}>
            Points expire after (months, 0 = never)
            <input type="number" value={s.points_expiry_months} data-testid="setting-expiry"
              onChange={(e) => setS({ ...s, points_expiry_months: Number(e.target.value) })}
              className="h-9 px-2 ml-2 w-20 rounded-md border text-[12.5px]" style={{ borderColor: HC.border }} />
          </label>
        </div>
      </div>

      <div className="rounded-2xl bg-white border p-5 space-y-4" style={{ borderColor: HC.border }} data-testid="crm-integrations">
        <h3 className="font-brand font-bold text-base" style={{ color: HC.text }}>Marketing &amp; analytics integrations</h3>
        <div className="text-[12px]" style={{ color: HC.soft }}>
          CRM emits customer, purchase, loyalty and campaign events to an outbox. Until a platform is enabled with real
          credentials, events are stored as <strong>not configured</strong> — no external call is faked.
        </div>
        {["clevertap", "google_analytics"].map((k) => (
          <div key={k} className="rounded-xl border p-3 space-y-2" style={{ borderColor: HC.border }}>
            <label className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: HC.text }}>
              <input type="checkbox" checked={!!s.integrations[k]?.enabled} data-testid={`integration-${k}-enabled`}
                onChange={(e) => setInt(k, "enabled", e.target.checked)} />
              {k === "clevertap" ? "CleverTap" : "Google Analytics"}
              <Pill status={s.integrations[k]?.enabled ? "active" : "draft"} text={s.integrations[k]?.enabled ? "Enabled" : "Not connected"} />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {(k === "clevertap" ? ["account_id", "passcode", "region"] : ["measurement_id", "api_secret"]).map((f) => (
                <input key={f} placeholder={f.replace(/_/g, " ")} value={s.integrations[k]?.[f] || ""}
                  data-testid={`integration-${k}-${f}`} onChange={(e) => setInt(k, f, e.target.value)}
                  className="h-9 px-2 rounded-md border text-[12.5px]" style={{ borderColor: HC.border }} />
              ))}
            </div>
          </div>
        ))}
        <Btn onClick={save} data-testid="crm-settings-save"><Save size={14} /> Save settings</Btn>
      </div>

      {events && (
        <div className="space-y-2">
          <h3 className="font-brand font-bold text-base" style={{ color: HC.text }}>Event outbox ({events.total})</h3>
          <div className="text-[11.5px]" style={{ color: HC.soft }}>
            Catalogue: {events.event_catalogue.join(" · ")}
          </div>
          <DataTable testid="crm-events-table" rows={events.rows}
            columns={[["occurred_at", "When"], ["event", "Event"], ["dispatch_status", "Dispatch"], ["targets", "Targets"]]}
            render={{ occurred_at: (r) => dt(r.occurred_at), targets: (r) => (r.targets || []).join(", ") || "—",
                      dispatch_status: (r) => <Pill status={r.dispatch_status === "pending" ? "pending" : "draft"} text={r.dispatch_status} /> }}
            empty="No events emitted yet" />
        </div>
      )}

      {audit && (
        <div className="space-y-2">
          <h3 className="font-brand font-bold text-base" style={{ color: HC.text }}>CRM audit trail ({audit.total})</h3>
          <DataTable testid="crm-audit-table" rows={audit.rows}
            columns={[["timestamp", "When"], ["user_name", "User"], ["action", "Action"], ["record", "Record"], ["ip", "IP"]]}
            render={{ timestamp: (r) => dt(r.timestamp), record: (r) => String(r.record || "").slice(0, 40) }}
            empty="No CRM audit entries yet" />
        </div>
      )}
    </div>
  );
}
